import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Wind, Layers, LayoutGrid, ArrowDownUp, Grid2x2, Fan, Building2, Building, Box, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    icon: Wind,
    tag: "1x1",
    title: "Sistemas Split 1x1",
    desc: "La solución ideal para climatizar estancias individuales con la máxima eficiencia energética A+++. Instalación rápida, silenciosa y con un acabado totalmente integrado en su hogar u oficina.",
    testid: "service-card-1x1",
  },
  {
    icon: Layers,
    tag: "Multi-Split",
    title: "Sistemas Multi-Split",
    desc: "Una única unidad exterior alimenta varias estancias con control de temperatura independiente, en cualquier combinación: 2x1, 3x1, 4x1 o superiores. Confort total con el mínimo impacto estético en la fachada.",
    testid: "service-card-multi",
  },
  {
    icon: LayoutGrid,
    tag: "Conductos",
    title: "Climatización por Conductos",
    desc: "Instalación centralizada e invisible integrada en el falso techo, con rejillas de difusión discretas y zonificación. La opción más elegante para viviendas completas y locales comerciales.",
    testid: "service-card-conductos",
  },
  {
    icon: ArrowDownUp,
    tag: "Suelo / Techo",
    title: "Climatización Suelo/Techo",
    desc: "Unidades consola de suelo o suspendidas en techo, ideales para locales comerciales, oficinas y espacios sin falso techo que requieren gran caudal de aire y una distribución uniforme.",
    testid: "service-card-suelo-techo",
  },
  {
    icon: Grid2x2,
    tag: "Cassette",
    title: "Climatización Cassette",
    desc: "Unidad empotrada en el falso techo con difusión en cuatro direcciones y mínima presencia visual. La solución profesional para oficinas, tiendas y restaurantes.",
    testid: "service-card-cassette",
  },
  {
    icon: Fan,
    tag: "Fancoil",
    title: "Fancoil",
    desc: "Climatización por agua con unidades fancoil vistas u ocultas: máximo confort, funcionamiento silencioso y eficiencia en viviendas, hoteles y edificios con instalación centralizada.",
    testid: "service-card-fancoil",
  },
  {
    icon: Building2,
    tag: "VRV",
    title: "Sistemas VRV",
    desc: "Climatización de volumen de refrigerante variable para grandes superficies: una instalación centralizada que climatiza múltiples zonas con control independiente y máxima eficiencia.",
    testid: "service-card-vrv",
  },
  {
    icon: Building,
    tag: "VRF",
    title: "Sistemas VRF",
    desc: "Flujo de refrigerante variable para edificios comerciales, oficinas y grandes viviendas: potencia escalable, recuperación de calor y gestión inteligente de cada estancia.",
    testid: "service-card-vrf",
  },
  {
    icon: Box,
    tag: "Monobloque",
    title: "Monobloque",
    desc: "Unidades compactas sin unidad exterior separada ni conexiones frigoríficas: la solución más limpia y rápida para viviendas, apartamentos y espacios con limitaciones de fachada.",
    testid: "service-card-monobloque",
  },
];

export const Services = () => (
  <section id="servicios" data-testid="services-section" className="bg-ocean-50/50 py-16 sm:py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-ocean-600">
            Servicios de Instalación
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl text-balance">
            Equipos para cada espacio, instalados sin improvisación
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/presupuesto"
            data-testid="services-quote-link"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-ocean-700 transition-colors hover:text-ember"
          >
            Solicitar presupuesto de cualquier sistema
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>

      <Reveal className="mt-12" delay={0.08}>
        <div className="relative overflow-hidden rounded-[2rem] shadow-xl ring-1 ring-slate-200">
          <img
            src="/assets/equipo-instalando.jpg"
            alt="Dos técnicos de Clima Oliver con uniforme corporativo instalando un split en una vivienda frente al mar"
            className="aspect-[2/1] w-full object-cover"
            data-testid="services-team-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
          <span className="absolute bottom-5 left-5 rounded-full bg-white/95 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-navy shadow-lg backdrop-blur">
            Nuestro equipo en acción · Costa de Tarragona
          </span>
        </div>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.12}>
            <article
              data-testid={s.testid}
              className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-ocean-600/30 hover:shadow-xl hover:shadow-ocean-600/5 sm:p-8"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ocean-50 text-ocean-600 transition-colors duration-300 group-hover:bg-ocean-600 group-hover:text-white">
                  <s.icon className="h-6 w-6" />
                </span>
                <span className="rounded-full bg-ember/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-ember">
                  {s.tag}
                </span>
              </div>
              <h3 className="mt-6 font-display text-xl font-bold text-navy sm:text-2xl">
                {s.title}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
